import { captureErrorWithPayload } from "utils/error";
import { createLogger } from "utils/logging";
import { BenefitElement, buildSearchParams, defineElement, callBackend } from "../benefit_base";
import { DetailviewService } from "./DetailviewService";
import { TrackingService } from "./tracking/TrackingService";

const ATTRIBUTE_NAMES = {
	VARIATION_ID: "variation-id",
	PRODUCT_ID: "product-id",
	EVENT_MERGE_ID: "event-merge-id",
	FEATURE: "feature",
	TEAM: "team",
	IS_PREFETCHED: "is-prefetched",
};

const searchParamBenefitId = "benefitId";
const searchParamBenefitActiveUntil = "benefitActiveUntil";
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const BENEFIT_ACTIVE_UNTIL_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/; /*                                          */

const log = createLogger("tag", "detailview.component");
const DETAILVIEW_ELEMENT_NAME = "ft9-detailview-v1";

const REQUEST_INITIATOR_FILE_NAME = new URL(import.meta.url).pathname.split("/").pop() ?? "unknown";

const ERROR_KEY = "ft9-benefit-detailview-not-ok";
const ERROR_LABELS = { service: "tag", module: "detailview", team: "ft9" };

export class DetailviewElement extends BenefitElement {
	private readonly detailviewService: DetailviewService;
	private autoActivateBenefitId: string | undefined;

	constructor() {
		super(); /*                                     */
		this.detailviewService = new DetailviewService(new TrackingService());
	}

	getAutoactiveBenefitId() {
		/*                                              */
		return this.autoActivateBenefitId;
	}

	protected async hydrate(): Promise<void> {
		if (this.getAttribute(ATTRIBUTE_NAMES.IS_PREFETCHED) === "true") {
			log.info("Detailview is prefetched, skipping backend call.");
			this.initDetailview();
			/*                                                                                                                            */
			this.removeAttribute(ATTRIBUTE_NAMES.IS_PREFETCHED);
			return;
		}

		this.replaceChildren(); /*                       */

		const variationId = this.getAttribute(ATTRIBUTE_NAMES.VARIATION_ID);
		if (!variationId) {
			log.error("No variationId provided, cannot load detailview.");
			captureErrorWithPayload({ message: "No variationId provided", type: ERROR_KEY, labels: ERROR_LABELS });
			return;
		}

		const autoActivateParams = this.getAutoActivateParams();

		const params = {
			variationId,
			productId: this.getAttribute(ATTRIBUTE_NAMES.PRODUCT_ID),
			...autoActivateParams,
		};
		const urlSearchParams = buildSearchParams(params);
		log.info("Calling tag for %s with %s", this.id || "id-not-set", urlSearchParams.toString());

		try {
			const url = new URL(`/benefit-tag/detailview?${urlSearchParams}`, globalThis.location.origin);
			const options = {
				url,
				feature: this.getAttribute(ATTRIBUTE_NAMES.FEATURE),
				team: this.getAttribute(ATTRIBUTE_NAMES.TEAM),
				initiator: REQUEST_INITIATOR_FILE_NAME,
				priority: "high" as const,
				logging: {
					logger: log,
					errorKey: ERROR_KEY,
					errorLabels: ERROR_LABELS,
				},
			};
			const response = await callBackend(options);

			if (response.status === 200) {
				this.innerHTML = await response.text();
				this.initDetailview();
				return;
			}

			this.detailviewService.handleNoContentDetailview(variationId, this);
		} catch {
			/*                                                                    */
			/*                                                                          */
		}
	}

	private getAutoActivateParams(): { benefitId: string | undefined; autoActivateBenefitActiveUntil: string | undefined } {
		const pageUrlSearchParams = new URLSearchParams(window.location.search);

		const benefitId = pageUrlSearchParams.get(searchParamBenefitId);
		if (benefitId && UUID_REGEX.test(benefitId)) {
			this.autoActivateBenefitId = benefitId;
		}
		const benefitActiveUntil = pageUrlSearchParams.get(searchParamBenefitActiveUntil);
		const hasValidBenefitActiveUntil = benefitActiveUntil !== null && BENEFIT_ACTIVE_UNTIL_REGEX.test(decodeURIComponent(benefitActiveUntil));

		return {
			benefitId: this.autoActivateBenefitId,
			autoActivateBenefitActiveUntil: this.autoActivateBenefitId && hasValidBenefitActiveUntil ? decodeURIComponent(benefitActiveUntil) : undefined,
		};
	}

	private initDetailview() {
		const variationId = this.getAttribute(ATTRIBUTE_NAMES.VARIATION_ID);
		const eventMergeId = this.getAttribute(ATTRIBUTE_NAMES.EVENT_MERGE_ID);

		if (!variationId) {
			return;
		}

		this.detailviewService.initialize(variationId, this, eventMergeId);
	}
}

defineElement(DetailviewElement, DETAILVIEW_ELEMENT_NAME, "ftnine_emergency_custom_element_disable_detailview");
